import { Button } from "@/components/ui/button";
import { ConfirmacaoPacoteGiro } from "@/components/ConfirmacaoPacoteGiro";
import { PacotePromocionalDto, SorteioGiroDto } from "@/dtos/BuscarSorteioVigenteDto";
import { labelPlural } from "@/utils/copyTituloBilhete";
import {
    aplicarSelecaoPorQuantidade,
    calcularNudgePacoteGiro,
    criarSelecaoPacote,
    criarSelecaoQuantidade,
} from "@/utils/selecaoPacoteGiro";
import { Minus, Plus } from "lucide-react";
import { SelecaoCompraGiro } from "@/components/CompraRapidaGiro";
import { EtapasCompraRapidaGiro } from "..";

interface EtapaSelecionarQuantidadeGiroProps {
    sorteioGiro: SorteioGiroDto;
    selecao: SelecaoCompraGiro;
    quantidade: number;
    quantidadeAvulsaAnterior: number;
    pacoteSelecionado: PacotePromocionalDto | null;
    alterarEtapa: (etapa: EtapasCompraRapidaGiro) => void;
    onSelecaoChange: (
        selecao: SelecaoCompraGiro,
        quantidade: number,
        pacote: PacotePromocionalDto | null
    ) => void;
    vendaBloqueada?: boolean;
}

export function EtapaSelecionarQuantidadeGiro({
    sorteioGiro,
    selecao,
    quantidade,
    quantidadeAvulsaAnterior,
    pacoteSelecionado,
    alterarEtapa,
    onSelecaoChange,
    vendaBloqueada,
}: EtapaSelecionarQuantidadeGiroProps) {
    const qtdMinima = sorteioGiro.QtdMinima || 1;
    const pacotes = sorteioGiro.PacotesPromocionais ?? [];
    const valorUnitario = sorteioGiro.Valor || 0;

    const nudge =
        selecao.modo === "quantidade"
            ? calcularNudgePacoteGiro(quantidade, valorUnitario, pacotes)
            : null;

    function ajustarQuantidade(novaQtd: number) {
        const resultado = aplicarSelecaoPorQuantidade(
            novaQtd,
            qtdMinima,
            valorUnitario,
            pacotes
        );
        onSelecaoChange(resultado.selecao, resultado.quantidade, resultado.pacote);
    }

    function handleSelecionarPacote(pacote: PacotePromocionalDto) {
        onSelecaoChange(criarSelecaoPacote(pacote), pacote.Qtd, pacote);
    }

    function handleNudgeClick() {
        if (!nudge) {
            return;
        }
        handleSelecionarPacote(nudge.pacote);
    }

    function handleAlterarSelecao() {
        const qtd = Math.max(qtdMinima, quantidadeAvulsaAnterior);
        onSelecaoChange(criarSelecaoQuantidade(qtd, valorUnitario), qtd, null);
    }

    if (pacoteSelecionado) {
        return (
            <ConfirmacaoPacoteGiro
                pacote={pacoteSelecionado}
                ctaLabel="Continuar"
                disabled={vendaBloqueada}
                ctaClassName="mt-2 w-40"
                onContinuar={() => alterarEtapa(EtapasCompraRapidaGiro.InserirCPF)}
                onAlterarSelecao={handleAlterarSelecao}
            />
        );
    }

    return (
        <div className="flex flex-col items-center justify-center gap-4">
            <h1 className="text-center text-primary font-semibold text-xl">
                Quantidade de {labelPlural()}
            </h1>

            {pacotes.length > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-2 px-2">
                    {pacotes.map((pacote) => (
                        <Button
                            key={pacote.IdPacotePromocional}
                            type="button"
                            variant="outline"
                            onClick={() => handleSelecionarPacote(pacote)}
                        >
                            {pacote.Qtd} {labelPlural()}
                            {pacote.Valor > 0 && (
                                <span className="ml-1 text-xs opacity-90">
                                    {pacote.Valor.toLocaleString("pt-br", {
                                        style: "currency",
                                        currency: "BRL",
                                    })}
                                </span>
                            )}
                        </Button>
                    ))}
                </div>
            )}

            <div className="flex items-center justify-center gap-3">
                <Button
                    type="button"
                    disabled={quantidade <= qtdMinima}
                    onClick={() => ajustarQuantidade(quantidade - 1)}
                    size="lg"
                    variant="outline"
                >
                    <Minus size={20} />
                </Button>
                <span className="flex flex-col select-none justify-center items-center text-primary text-sm font-semibold">
                    <span>{quantidade}</span>
                    <span>{labelPlural()}</span>
                </span>
                <Button
                    type="button"
                    onClick={() => ajustarQuantidade(quantidade + 1)}
                    size="lg"
                    variant="outline"
                >
                    <Plus size={20} />
                </Button>
            </div>

            {nudge && (
                <button
                    type="button"
                    onClick={handleNudgeClick}
                    className="text-sm text-primary underline hover:no-underline text-center px-2"
                >
                    {nudge.mensagem}
                </button>
            )}

            {selecao.valor > 0 && (
                <p className="text-sm text-muted-foreground">
                    Total:{" "}
                    {selecao.valor.toLocaleString("pt-br", {
                        style: "currency",
                        currency: "BRL",
                    })}
                </p>
            )}

            <Button
                disabled={selecao.quantidade === 0 || vendaBloqueada}
                type="button"
                onClick={() => alterarEtapa(EtapasCompraRapidaGiro.InserirCPF)}
                variant="foreground"
                className="mt-2 w-40"
            >
                {vendaBloqueada ? "Venda indisponível" : "Continuar"}
            </Button>
        </div>
    );
}
